import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FilePlus2 } from "lucide-react"

import { DataState } from "@/components/DataState"
import { SearchInput, Toolbar, ToolbarField } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { useI18n, t } from "@/lib/i18n"
import type { JournalEntry } from "@/lib/types"
import { useTableKeyboardNav } from "@/lib/useTableKeyboardNav"
import { cn } from "@/lib/utils"

import { AdjustmentEntryDialog } from "@/routes/books/AdjustmentEntryDialog"
import { onWriteError } from "@/lib/writeErrors"

import {
  createAdjustmentEntry,
  fetchAccounts,
  fetchJournal,
  type AdjustmentEntryPayload,
} from "@/routes/books/api"
import { refAsiento } from "@/routes/comentarios/api"
import { useBooksFilters } from "@/routes/books/context"
import { BotonDescargar } from "@/components/BotonDescargar"
import { bookToApi } from "@/routes/books/mapping"

const ORIGIN_META: Record<
  JournalEntry["origin"],
  { label: string; variant: "outline" | "secondary" | "warning-soft" | "brand-soft" }
> = {
  automatico: { label: "Automatico", variant: "outline" },
  manual: { label: "Manual", variant: "secondary" },
  ajuste: { label: "Ajuste", variant: "warning-soft" },
  cierre: { label: "Cierre", variant: "brand-soft" },
}

export function JournalPage() {
  const { book, from, to } = useBooksFilters()
  const { activeTenantId, activeEntityId } = useAuth()
  const { can } = useRbac()
  const { t } = useI18n()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [origin, setOrigin] = React.useState("todos")
  const [query, setQuery] = React.useState("")
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["journal", activeTenantId, book, from, to, origin, query],
    queryFn: () => fetchJournal({ book, from, to, origin, query }),
  })

  const entries = data ?? []
  const nav = useTableKeyboardNav({ count: entries.length })

  // El plan de cuentas real: el desplegable del asiento de ajuste tiene que
  // ofrecer codigos que existan en `account_local`, o el POST muere en 422.
  const accountsQuery = useQuery({
    queryKey: ["accounts", activeTenantId],
    queryFn: fetchAccounts,
  })

  // Los totales salen de la cabecera que ya sumo el backend, no de las lineas:
  // el listado del diario no las trae (ver books/mapping.ts).
  const totals = React.useMemo(
    () =>
      entries.reduce(
        (acc, entry) => {
          acc.debit += entry.totalDebit
          acc.credit += entry.totalCredit
          return acc
        },
        { debit: 0, credit: 0 },
      ),
    [entries],
  )

  const createMutation = useMutation({
    mutationFn: (payload: AdjustmentEntryPayload) => createAdjustmentEntry(payload),
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Asiento posteado",
        description: "Queda registrado con autor, fecha y motivo.",
      })
      setDialogOpen(false)
      void queryClient.invalidateQueries({ queryKey: ["journal"] })
    },
    // Un asiento que no se posteo y el contador cree que si es el peor fallo de
    // esta pantalla: cuadra el mes contra un numero que no existe.
    onError: onWriteError("el asiento de ajuste", "postear"),
  })

  const hasFilters = query.trim().length > 0 || origin !== "todos"

  return (
    <div className="space-y-3">
      <Toolbar>
        <ToolbarField label={t("Buscar")} htmlFor="q-journal" className="min-w-[18rem] flex-1">
          <SearchInput
            id="q-journal"
            value={query}
            onValueChange={setQuery}
            placeholder={t("Numero de asiento, glosa, documento o autor")}
            className="w-full"
          />
        </ToolbarField>

        <ToolbarField label={t("Origen")} htmlFor="f-origen-diario">
          <Select value={origin} onValueChange={setOrigin}>
            <SelectTrigger id="f-origen-diario" className="w-44">
              <SelectValue placeholder={t("Origen")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">{t("Todos los origenes")}</SelectItem>
              <SelectItem value="automatico">{t("Automatico")}</SelectItem>
              <SelectItem value="manual">{t("Manual")}</SelectItem>
              <SelectItem value="ajuste">{t("Ajuste")}</SelectItem>
              <SelectItem value="cierre">{t("Cierre")}</SelectItem>
            </SelectContent>
          </Select>
        </ToolbarField>

        <div className="ml-auto flex items-end gap-2 pb-px">
          <Button size="sm" disabled={!can("edit")} onClick={() => setDialogOpen(true)}>
            <FilePlus2 />
            {t("Nuevo asiento de ajuste")}</Button>
        </div>
      </Toolbar>

      <WorkPanel
        actions={
          <BotonDescargar
            recurso="diario"
            filtros={{ entity_id: activeEntityId, libro: bookToApi(book), desde: from, hasta: to }}
          />
        }
        title={t("libro_diario")}
        meta={
          <>
            <Badge variant="outline" mono>
              {entries.length} asientos
            </Badge>
            <span className="text-2xs text-muted-foreground">
              {t("debe")} <span className="num font-medium text-foreground">{formatAmount(totals.debit)}</span>
              {" · "}
              {t("haber")} <span className="num font-medium text-foreground">{formatAmount(totals.credit)}</span>
            </span>
          </>
        }
        footnote={t("Cada asiento se muestra con su cabecera y, cuando el listado las trae, sus lineas; el detalle completo esta a un clic en el mayor. Un asiento que no cuadra queda marcado en la cabecera: el sistema lo asienta igual para que se vea, no lo esconde.")}
      >
        <DataState
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={entries.length === 0}
          emptyMessage={
            hasFilters
              ? "Ningun asiento coincide con estos filtros."
              : "No hay asientos en el rango seleccionado."
          }
          emptyHint={
            hasFilters
              ? "Prueba con otro origen o amplia la busqueda."
              : "Amplia el rango de fechas en la barra superior, o revisa si el periodo tiene comprobantes pendientes en la cola de revision."
          }
          columns={[8, 8, 8, 34, 10, 6, 10, 10, 6]}
        >
          <Table containerClassName="max-h-[calc(100vh-20rem)]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{t("Asiento")}</TableHead>
                <TableHead className="w-[5.5rem]">{t("Fecha")}</TableHead>
                <TableHead className="w-20">{t("cuenta")}</TableHead>
                <TableHead className="min-w-[18rem]">{t("Denominacion / glosa")}</TableHead>
                <TableHead className="w-28">{t("Documento")}</TableHead>
                <TableHead className="w-16">{t("C. costo")}</TableHead>
                <TableHead className="w-28 text-right">{t("debe")}</TableHead>
                <TableHead className="w-28 text-right">{t("haber")}</TableHead>
                <TableHead className="w-24">{t("Origen")}</TableHead>
                <TableHead className="w-28">{t("Autor")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody {...nav.bodyProps}>
              {entries.map((entry, index) => {
                const debit = entry.totalDebit
                const credit = entry.totalCredit
                const balanced = Math.abs(debit - credit) < 0.005
                const meta = ORIGIN_META[entry.origin]
                return (
                  <React.Fragment key={entry.id}>
                    {/* Cabecera del asiento: banda hundida, filete superior grueso. */}
                    <TableRow
                      {...nav.rowProps(index)}
                      className={cn(
                        "row-group outline-none [&>td]:border-t [&>td]:border-t-border-strong/70",
                        nav.cursor === index && "row-cursor",
                        !balanced && "bg-destructive-soft hover:bg-destructive-soft",
                      )}
                    >
                      <TableCell className="code font-semibold">{entry.number}</TableCell>
                      <TableCell className="code">{formatDate(entry.date)}</TableCell>
                      <TableCell />
                      <TableCell className="font-medium">
                        {entry.glosa}
                        {entry.reason ? (
                          <span className="ml-2 text-2xs font-normal text-muted-foreground">
                            {t("motivo:")}{" "}{entry.reason}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="code text-2xs text-muted-foreground">
                        {entry.documentRef ?? "—"}
                      </TableCell>
                      <TableCell />
                      <TableCell className="num-strong">{formatAmount(debit)}</TableCell>
                      <TableCell className="num-strong">{formatAmount(credit)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant={meta.variant}>{meta.label}</Badge>
                          {!balanced ? <Badge variant="destructive">{t("no cuadra")}</Badge> : null}
                        </div>
                      </TableCell>
                      <TableCell className="truncate text-2xs text-muted-foreground">
                        {entry.createdBy}
                      </TableCell>
                    </TableRow>

                    {/* Lineas: sangradas por la columna de cuenta, texto apagado. */}
                    {entry.lines.map((line) => (
                      <TableRow key={line.id} className="text-muted-foreground">
                        <TableCell />
                        <TableCell />
                        <TableCell className="code text-foreground">{line.accountCode}</TableCell>
                        <TableCell className="pl-4">
                          {line.accountName}
                          {line.memo ? <span className="ml-1 text-2xs">· {line.memo}</span> : null}
                        </TableCell>
                        <TableCell />
                        <TableCell className="code text-2xs">{line.costCenter ?? ""}</TableCell>
                        <TableCell
                          className={cn("num", line.debit ? "text-foreground" : "num-zero")}
                        >
                          {formatAmount(line.debit, { blankZero: true }) || "—"}
                        </TableCell>
                        <TableCell
                          className={cn("num", line.credit ? "text-foreground" : "num-zero")}
                        >
                          {formatAmount(line.credit, { blankZero: true }) || "—"}
                        </TableCell>
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    ))}

                    {/* El listado del backend no trae las lineas (GROUP BY je.id):
                        trae su conteo. Se dice cuantas hay y donde verlas, en vez
                        de dejar la cabecera colgando como si el asiento no tuviera
                        detalle. TODO(backend): json_agg de las lineas en
                        GET /ledger/journal. */}
                    {!entry.linesLoaded && entry.lineCount > 0 ? (
                      <TableRow className="text-muted-foreground">
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell className="pl-4 text-2xs italic">
                          {entry.lineCount}{" "}{t("lineas · el detalle se abre desde el mayor")}</TableCell>
                        <TableCell colSpan={6} />
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      <AdjustmentEntryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultBook={book}
        accounts={accountsQuery.data}
        pending={createMutation.isPending}
        onSubmit={(payload) => createMutation.mutateAsync(payload).then(() => undefined)}
      />
    </div>
  )
}
